import { SEARCH_TYPE } from '@/constants/searchType';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { FilterStore } from './types';
import { useSearchType, SearchType } from '@/hooks/publicSearch/useSearchType';
import {
  FieldOption,
  FilterGroup,
  FilterInput,
  LogicalOperator,
} from '@/components/publicSearch/constants';
import { hasPublicSearchUrlState } from '@/lib/publicSearchPaths';
import { buildQuickSearchFilterFromTerm } from '@/components/publicSearch/Search/quickSearchFilterBuilder';

const CONFIG = {
  [SEARCH_TYPE.ANIMAL]: {
    storeName: 'AnimalFilterStore',
    searchableFields: [FieldOption.searchTerm.value],
  },
  [SEARCH_TYPE.MEMBERS]: {
    storeName: 'MembersFilterStore',
    searchableFields: [FieldOption.member.value],
  },
  [SEARCH_TYPE.SALE_CATALOGUES]: {
    storeName: 'SaleCataloguesFilterStore',
    searchableFields: [FieldOption.saleName.value],
  },
  [SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS]: {
    storeName: 'SaleCataloguesDetailAnimalsFilterStore',
    searchableFields: [FieldOption.searchTerm.value],
  },
  [SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS]: {
    storeName: 'SemenCataloguesDetailAnimalsFilterStore',
    searchableFields: [FieldOption.searchTerm.value],
  },
  [SEARCH_TYPE.SALE_CATALOGUES_NOTES]: {
    storeName: 'SaleCataloguesNotesFilterStore',
    searchableFields: [],
  },
  [SEARCH_TYPE.MATING]: {
    storeName: 'MatingFilterStore',
    searchableFields: [FieldOption.searchTerm.value],
  },
  [SEARCH_TYPE.DOWNLOADS]: {
    storeName: 'DownloadsFilterStore',
    searchableFields: [],
  },
  [SEARCH_TYPE.ONLINE_REGISTRATIONS]: {
    storeName: 'OnlineRegistrationsFilterStore',
    searchableFields: [],
  },
} as const;

interface FilterStoreConfig {
  storeName: string;
  searchableFields: readonly string[];
}

export const createFilterStore = (config: FilterStoreConfig) => {
  const { storeName, searchableFields } = config;

  return create<FilterStore>()(
    persist(
      devtools(
        immer((set, _get) => ({
          commonFilterOperator: LogicalOperator.or,
          filters: [],
          filterGroups: [] as FilterGroup[],
          groupsOperator: LogicalOperator.or,

          setFilters(filters: FilterInput[]) {
            set((state) => {
              state.filters = filters;
            });
          },

          updateCommonFilterOperator: (operator) =>
            set((state) => {
              state.commonFilterOperator = operator;
            }),

          addFilter: (filter) =>
            set((state) => {
              state.filters = [...state.filters, filter];
            }),

          updateFilter: (index, filter) =>
            set((state) => {
              if (index !== -1) {
                state.filters[index] = {
                  ...state.filters[index]!,
                  ...filter,
                };
              }
            }),

          removeFilter: (index) =>
            set((state) => {
              state.filters = state.filters.filter((_, i) => i !== index);
            }),

          clearFilter: () =>
            set((state) => {
              state.filters = [];
              state.commonFilterOperator = LogicalOperator.or;
              state.filterGroups = [];
              state.groupsOperator = LogicalOperator.or;
            }),

          computeFiltersFromSearchTerm: (searchTerm) => {
            const trimmed = searchTerm.trim();

            if (!trimmed) {
              set((state) => {
                state.commonFilterOperator = LogicalOperator.or;
                state.filters = [];
              });
              return;
            }

            const defaultSearchRelatedFilterInputs: FilterInput[] = [];
            for (const field of searchableFields) {
              const built = buildQuickSearchFilterFromTerm(trimmed, field);
              if (built) {
                defaultSearchRelatedFilterInputs.push(built);
              }
            }

            if (defaultSearchRelatedFilterInputs.length === 0) {
              return;
            }

            set((state) => {
              state.commonFilterOperator = LogicalOperator.or;
              state.filters = defaultSearchRelatedFilterInputs;
            });
          },

          setFilterGroups: (groups) =>
            set((state) => {
              state.filterGroups = groups;
            }),

          setGroupsOperator: (op) =>
            set((state) => {
              state.groupsOperator = op;
            }),

          addFilterGroup: () =>
            set((state) => {
              state.filterGroups.push({
                filters: [{ field: '', operator: '', value: '' }],
                logicalOperator: LogicalOperator.and,
              });
            }),

          removeFilterGroup: (groupIndex) =>
            set((state) => {
              state.filterGroups = state.filterGroups.filter(
                (_, i) => i !== groupIndex,
              );
            }),

          addFilterToGroup: (groupIndex, filter) =>
            set((state) => {
              const group = state.filterGroups[groupIndex];
              if (group) {
                group.filters.push(filter);
              }
            }),

          updateFilterInGroup: (groupIndex, filterIndex, patch) =>
            set((state) => {
              const group = state.filterGroups[groupIndex];
              if (group && group.filters[filterIndex]) {
                group.filters[filterIndex] = {
                  ...group.filters[filterIndex]!,
                  ...patch,
                };
              }
            }),

          removeFilterFromGroup: (groupIndex, filterIndex) =>
            set((state) => {
              const group = state.filterGroups[groupIndex];
              if (group) {
                group.filters = group.filters.filter(
                  (_, i) => i !== filterIndex,
                );
                // Never leave a group with zero rows — reset to a single empty
                // placeholder so there is always one filter line to fill in.
                if (group.filters.length === 0) {
                  group.filters = [{ field: '', operator: '', value: '' }];
                }
              }
            }),

          updateGroupOperator: (groupIndex, op) =>
            set((state) => {
              const group = state.filterGroups[groupIndex];
              if (group) {
                group.logicalOperator = op;
              }
            }),

          clearFilterGroups: () =>
            set((state) => {
              state.filterGroups = [];
              state.groupsOperator = LogicalOperator.or;
            }),
        })),
        { name: storeName },
      ),
      {
        name: storeName,
        storage: createJSONStorage(() => sessionStorage),
        // Defer to the URL: without `?search=` state, drop the previous
        // session's filters so a fresh visit starts empty rather than
        // re-running the stale query. See useSearchStore for the rationale.
        merge: (persisted, current) => {
          const merged = {
            ...current,
            ...(persisted as Partial<FilterStore>),
          };
          if (hasPublicSearchUrlState()) return merged;
          return {
            ...merged,
            filters: [],
            filterGroups: [],
            commonFilterOperator: LogicalOperator.or,
            groupsOperator: LogicalOperator.or,
          };
        },
      },
    ),
  );
};

const storeCache = new Map<SearchType, ReturnType<typeof createFilterStore>>();

export function getFilterStore(searchType: SearchType | string) {
  // Legacy: semen-catalogues list was merged into sale-catalogues
  if (searchType === SEARCH_TYPE.SEMEN_CATALOGUES) {
    searchType = SEARCH_TYPE.SALE_CATALOGUES;
  }
  if (!CONFIG[searchType as SearchType]) {
    searchType = SEARCH_TYPE.ANIMAL;
  }

  const key = searchType as SearchType;
  if (storeCache.has(key)) {
    return storeCache.get(key)!;
  }

  const config = CONFIG[key];
  const store = createFilterStore(config);
  storeCache.set(key, store);

  return store;
}

export default function useFilterStore(customType?: SearchType) {
  const searchType = useSearchType();
  const store = getFilterStore(customType || searchType);
  return store();
}
